// import '../styles/globals.css';
import { useEffect } from 'react';

import { withTranslateRoutes } from 'next-translate-routes';

import { ToastrProvider } from '@components/organisms/Toastr';

import { ApolloProvider } from '@apollo/client';
import { useAuthenticatedApolloClient } from '@sdk/auth/useAuthenticatedApolloClient';
import {
  SaleorAuthProvider,
  useAuthChange,
  useSaleorAuthClient,
} from '@sdk/auth';
import { CheckoutProvider } from '@sdk/providers/CheckoutProvider';

import type { AppProps } from 'next/app';
import { ThemeProvider } from 'styled-components';

import { defaultTheme, GlobalStyle } from '@styles';
import { LocaleProvider } from '@components/containers/Locale';

import { useRouter } from 'next/router';

import { useLocalStorage } from '@hooks';
import NiceModal from '@ebay/nice-modal-react';
import { SubscribeModal } from '@components/overlays/Subscribe';

import Head from 'next/head';
import { apiUrl, subscribeNotification } from '../constants';

import {
  App as StorefrontApp,
  PreconnectManifest,
  PreloadedFonts,
} from '../app';

function App({ Component, pageProps }: AppProps) {
  const { asPath, basePath } = useRouter();
  const [subscribeTimeout, setSubscribeTimeout] = useLocalStorage(
    'subscribeTimeout',
    null,
    { sync: true }
  );

  const useSaleorAuthClientProps = useSaleorAuthClient({
    saleorApiUrl: apiUrl,
    storage: typeof window !== 'undefined' ? window.localStorage : undefined,
  });

  const { saleorAuthClient } = useSaleorAuthClientProps;

  const { apolloClient, resetClient } = useAuthenticatedApolloClient(
    saleorAuthClient.fetchWithAuth
  );

  useAuthChange({
    onSignedOut: () => resetClient(),
    onSignedIn: () =>
      apolloClient.refetchQueries({
        include: ['UserDetails', 'Wishlist'],
      }),
  });

  // show modal in 1 min after mounted
  useEffect(() => {
    if (subscribeNotification) {
      if (subscribeTimeout < Date.now()) {
        const timer = setTimeout(() => {
          NiceModal.show(SubscribeModal);
        }, 60000);
        // ensure the Modal is only show once 7 days
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 7);
        setSubscribeTimeout(tomorrow.getTime());
        // clear timer if the component unmount
        return () => clearTimeout(timer);
      }
    }
  }, []);

  const icon180 = `${basePath}/apple-touch-icon.png`;
  const icon32 = `${basePath}/favicon-32x32.png`;
  const icon16 = `${basePath}/favicon-16x16.png`;
  const icon = `${basePath}/favicon.png`;
  const iconsafari = `${basePath}/safari-pinned-tab.svg`;
  const manifest = `${basePath}/manifest.json`;

  const path = asPath.split('?')[0];

  return (
    <>
      <Head>
        <PreloadedFonts />
        <PreconnectManifest />
        <link rel="apple-touch-icon" sizes="180x180" href={icon180} />
        <link rel="icon" type="image/png" sizes="32x32" href={icon32} />
        <link rel="icon" type="image/png" sizes="16x16" href={icon16} />
        <link rel="icon" type="image/png" href={icon} />
        <link rel="manifest" href={manifest} />
        <link rel="mask-icon" href={iconsafari} color="#5bbad5" />
        <meta name="msapplication-TileColor" content="#da532c" />
        <meta name="theme-color" content="#ffffff" />
      </Head>
      <ThemeProvider theme={defaultTheme}>
        <ToastrProvider>
          <LocaleProvider>
            <GlobalStyle />
            <SaleorAuthProvider {...useSaleorAuthClientProps}>
              <ApolloProvider client={apolloClient}>
                <CheckoutProvider>
                  <StorefrontApp>
                    <Component key={path} {...pageProps} />
                  </StorefrontApp>
                </CheckoutProvider>
              </ApolloProvider>
            </SaleorAuthProvider>
          </LocaleProvider>
        </ToastrProvider>
      </ThemeProvider>
    </>
  );
}

export default withTranslateRoutes(JSON.parse(`{"defaultLocale":"en","locales":["en","fr","de"],"routesTree":{"name":"","paths":{"default":""},"children":[{"name":"account-confirm","paths":{"default":"account-confirm"}},{"name":"index","paths":{"default":"index"}},{"name":"order-finalized","paths":{"default":"order-finalized"}},{"name":"reset-password","paths":{"default":"reset-password"}},{"name":"wishlist","paths":{"default":"wishlist"}},{"name":"account","paths":{"default":"account"},"children":[{"name":"address-book","paths":{"default":"address-book"}},{"name":"index","paths":{"default":"index"}},{"name":"order-history","paths":{"default":"order-history"},"children":[{"name":"index","paths":{"default":"index"}},{"name":"[token]","paths":{"default":":token"}}]}]},{"name":"architect","paths":{"default":"architect","fr":"architecte","de":"architekt","pl":"architekt"},"children":[{"name":"index","paths":{"default":"index"}}]},{"name":"blog","paths":{"default":"blog"},"children":[{"name":"index","paths":{"default":"index"}},{"name":"[slug]","paths":{"default":":slug"}}]},{"name":"cart","paths":{"default":"cart"},"children":[{"name":"index","paths":{"default":"index"}},{"name":"redirect","paths":{"default":"redirect"},"children":[{"name":"[token]","paths":{"default":":token"}}]}]},{"name":"checkout","paths":{"default":"checkout"},"children":[{"name":"address","paths":{"default":"address"}},{"name":"index","paths":{"default":"index"}},{"name":"payment-confirm","paths":{"default":"payment-confirm"}},{"name":"payment","paths":{"default":"payment"}},{"name":"shipping","paths":{"default":"shipping"}}]},{"name":"collection","paths":{"default":"collection","en":"c/sliding-door","fr":"c/porte-coulissante","de":"c/schiebetuer","pl":"c/drzwi-przesuwne"},"children":[{"name":"[slug]","paths":{"default":":slug"},"children":[{"name":"[id]","paths":{"default":":id"}}]}]},{"name":"contacts","paths":{"default":"contacts","fr":"contactez-nous","de":"kontakt","pl":"kontakt"},"children":[{"name":"index","paths":{"default":"index"}}]},{"name":"inspirations","paths":{"default":"inspirations","de":"inspirationen","pl":"inspiracje"},"children":[{"name":"index","paths":{"default":"index"}},{"name":"[slug]","paths":{"default":":slug"}}]},{"name":"order-history","paths":{"default":"order-history"},"children":[{"name":"[token]","paths":{"default":":token"}}]},{"name":"page","paths":{"default":"page"},"children":[{"name":"[slug]","paths":{"default":":slug"}}]},{"name":"product","paths":{"default":"product"},"children":[{"name":"[slug]","paths":{"default":":slug"},"children":[{"name":"[id]","paths":{"default":":id"}}]}]},{"name":"reviews","paths":{"default":"reviews","fr":"avis","de":"bewertungen","pl":"opinie"},"children":[{"name":"index","paths":{"default":"index"}},{"name":"[slug]","paths":{"default":":slug"}}]},{"name":"server-sitemap-index.xml","paths":{"default":"server-sitemap-index.xml"},"children":[{"name":"index","paths":{"default":"index"}}]}]},"fallbackLng":{}}`), App);
